/**
 * Ассистент: панель, разговор и идущий ход.
 *
 * Стрим живёт здесь, а не в компоненте панели, и это главное решение файла.
 * Панель размонтируется при закрытии, а на переходе между задачей и доской
 * перерисовывается контент под ней; повесь запрос на жизненный цикл компонента
 * — и `onUnmounted` оборвал бы ответ на середине без единой ошибки на экране.
 * `AbortController` тоже здесь: остановить ход должно быть можно с любого
 * экрана, в том числе с закрытой панелью.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  cancelTurn,
  createThread,
  fetchQuota,
  listMessages,
  type ThreadMessage,
} from '@/features/assistant/api'
import { emptyPage, type PageDescriptor } from '@/features/assistant/context'
import { formatDetails, summarizeCall, type CallSummary } from '@/features/assistant/tools'
import {
  AssistantHttpError,
  streamTurn,
  threadPath,
  type AssistantDoneReason,
  type AssistantEvent,
  type AssistantQuota,
} from '@/features/assistant/stream'
import { i18n } from '@/i18n'

const WIDTH_KEY = 'tracker-assistant-width'

/** Ширины панели из макета: 8-1 нарисован на 380, 8-3a — на узких 320. */
export const MIN_WIDTH = 320
export const MAX_WIDTH = 640
export const DEFAULT_WIDTH = 380

/** Ширины, к которым панель липнет при перетаскивании кромки. */
export const SNAP_WIDTHS = [360, 480, 640]

/** Насколько близко к снапу нужно подвести кромку, чтобы её притянуло. */
const SNAP_DISTANCE = 16

/**
 * Реплика на экране. Та же форма, что у прочитанных из базы: разницы между
 * «только что сказали» и «загрузили из истории» на экране нет, и вторая форма
 * означала бы две ветки рендера вместо одной.
 */
export type PanelMessage = ThreadMessage & { kind: 'message' }

/** Строка-событие вызова инструмента (макет 8-2). */
export interface PanelCall {
  id: number
  kind: 'call'
  /** Идентификатор вызова: по нему результат находит свою строку. */
  callId: string
  name: string
  state: 'running' | 'done' | 'error'
  /** С чем инструмент позвали. Хранится сырым: подпись и подробности берут
   *  его и после результата, в котором аргументов может не быть. */
  args: unknown
  /**
   * Что инструмент вернул, сырьём. Нужен не панели, а экранам вокруг неё:
   * заметка узнаёт из выдачи `create_issues_batch`, какими ключами она
   * разобралась (DEV-1658), и пересказ в подпись эти ключи теряет.
   */
  result?: unknown
  summary: CallSummary
  durationMs: number
  /** Что ушло инструменту и что он вернул — под шевроном. */
  details: string
  /** Причина отказа для подсказки: на экране её место занимает ответ модели. */
  errorMessage: string
}

/** Черновик крупного действия, ждущий человека (макеты 8-3, 8-4). */
export interface PanelDraft {
  id: number
  kind: 'draft'
  /** Сообщение, которым подтверждают: ход продолжится отдельным запросом. */
  messageId: number
  callId: string
  name: string
  args: unknown
  state: 'pending' | 'applied' | 'declined'
}

/**
 * Что показывает панель, в порядке появления.
 *
 * Один список, а не «реплики отдельно, вызовы отдельно»: порядок между ними и
 * есть содержание экрана — модель сходила в поиск, сказала слово, сходила ещё
 * раз. Из двух списков он не восстанавливается ничем, кроме времени, которого у
 * событий потока нет.
 */
export type PanelItem = PanelMessage | PanelCall | PanelDraft

export const clampWidth = (width: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)))

/** Ближайший снап, если кромка подведена к нему вплотную; иначе как есть. */
export const snapWidth = (width: number): number => {
  const target = SNAP_WIDTHS.find((snap) => Math.abs(snap - width) <= SNAP_DISTANCE)

  return clampWidth(target ?? width)
}

const readWidth = (): number => {
  if (typeof window === 'undefined') return DEFAULT_WIDTH

  const saved = Number(window.localStorage.getItem(WIDTH_KEY))

  return saved ? clampWidth(saved) : DEFAULT_WIDTH
}

export const useAssistantStore = defineStore('assistant', () => {
  const isOpen = ref(false)
  const width = ref(readWidth())

  /**
   * Открытая страница. Единственный источник: чип берёт из него подпись, тред
   * — тип и ключ, ход — весь дескриптор целиком. Второй ref под контекст
   * разошёлся бы с этим на первом же переходе.
   */
  const page = ref<PageDescriptor>(emptyPage())
  const context = computed(() => page.value.context)

  const threadId = ref('')
  const items = ref<PanelItem[]>([])
  const quota = ref<AssistantQuota | null>(null)

  /**
   * Готовый текст ошибки, а не ключ перевода: причину отказа присылает
   * бэкенд («в этом разговоре уже идёт ответ»), и завернуть её в ключ нечем.
   * Свои сообщения берутся оттуда же, где чужие, — из `t`.
   */
  const error = ref('')

  /** Ход начат и ещё не закончился: композер показывает «Остановить». */
  const isStreaming = ref(false)

  /**
   * Ассистент не ответил (макет 8-5a).
   *
   * Отдельный признак, а не «есть текст ошибки»: сообщение остаётся на экране
   * и после того, как человек нажал «Повторить», — до конца нового хода. Гасить
   * блок нужно в момент повтора, а не в момент, когда ошибка стёрлась.
   */
  const isUnavailable = ref(false)

  /**
   * Сервер отказал по лимиту (429).
   *
   * Отдельно от остатка: счётчик на экране мог устареть — второй вкладкой,
   * другим устройством, — и отказ приходит раньше, чем панель об этом узнает.
   * Без этого признака исчерпанный лимит читался бы как поломка ассистента.
   */
  const overLimit = ref(false)

  /**
   * Пришла ли хоть одна порция текста. По этому признаку пропадает полоска
   * ожидания: между вопросом и первым словом модель ходит по инструментам, и
   * без полоски экран выглядит зависшим.
   */
  const hasText = ref(false)

  let controller: AbortController | null = null
  let nextId = 1
  let quotaRequest: Promise<void> | null = null

  const isEmpty = computed(() => items.value.length === 0)

  /**
   * Лимит на сегодня выбран (макет 8-5).
   *
   * `limit: 0` — лимита нет вовсе, и остаток в этом случае -1: «считать
   * нечего». Сравнивать с нулём без этой проверки значит закрыть ассистента
   * ровно тем, кому его открыли без ограничений.
   */
  const isBlocked = computed(() => {
    if (overLimit.value) return true

    const current = quota.value

    return Boolean(current && current.limit > 0 && current.remaining <= 0)
  })

  /**
   * Спросить остаток, не тратя обращение.
   *
   * Первое событие потока приезжает в ответ на вопрос, то есть слишком поздно:
   * решение «тратить ли последнее обращение» человек принимает до того, как
   * сформулирует вопрос. Запрос идёт один на сессию — дальше остаток
   * поддерживает сам поток.
   */
  const ensureQuota = async () => {
    if (quota.value || quotaRequest) return

    quotaRequest = fetchQuota()
      .then((state) => {
        quota.value = state

        if (state.remaining !== 0) overLimit.value = false
      })
      .catch(() => {
        // Остаток — подсказка, а не условие работы: не ответила ручка, панель
        // просто не показывает строку, а вопрос всё равно уйдёт.
      })
      .finally(() => {
        quotaRequest = null
      })

    await quotaRequest
  }

  const setWidth = (next: number) => {
    width.value = clampWidth(next)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(WIDTH_KEY, String(width.value))
    }
  }

  /**
   * Страница сменилась. Разговор при этом не трогается: человек ушёл с доски на
   * задачу посреди ответа, и обрывать ход из-за перехода — потерять ответ,
   * который уже пишется. Новый вопрос уедет уже с новым дескриптором.
   */
  const setPage = (next: PageDescriptor) => {
    page.value = next
  }

  const open = () => {
    isOpen.value = true
  }

  /** Закрытие прячет панель, но не трогает ход: ответ дописывается в стор. */
  const close = () => {
    isOpen.value = false
  }

  const toggle = () => {
    isOpen.value = !isOpen.value
  }

  /**
   * Дописать порцию ответа.
   *
   * Дописывается только в реплику, которая идёт последней: между двумя кусками
   * текста модель успевает сходить в инструмент, и продолжать абзац поверх
   * строки-события значило бы переставить сказанное во времени.
   */
  const appendText = (text: string) => {
    const last = items.value[items.value.length - 1]

    if (last?.kind === 'message' && last.role === 'assistant') last.text += text
    else items.value.push({ id: nextId++, kind: 'message', role: 'assistant', text })

    hasText.value = true
  }

  /** Инструмент вызван: строка появляется сразу, до результата. */
  const startCall = (event: AssistantEvent) => {
    items.value.push({
      id: nextId++,
      kind: 'call',
      callId: event.call_id ?? '',
      name: event.name ?? '',
      state: 'running',
      args: event.args,
      result: undefined,
      summary: null,
      durationMs: 0,
      details: formatDetails({ args: event.args }),
      errorMessage: '',
    })
  }

  /**
   * Инструмент ответил. Строка ищется по `call_id` с конца: в одном ходу модель
   * зовёт один и тот же инструмент по нескольку раз, и совпадение по имени
   * дописало бы результат в чужую строку.
   */
  const finishCall = (event: AssistantEvent) => {
    const callId = event.call_id ?? ''

    /** Аргументы берутся из вызова: в результате их может и не быть. */
    const settle = (args: unknown) => ({
      state: (event.error_type ? 'error' : 'done') as PanelCall['state'],
      args,
      result: event.result,
      summary: summarizeCall(args, event.result),
      durationMs: event.duration_ms ?? 0,
      details: formatDetails({ args, result: event.result }),
      errorMessage: event.message ?? '',
    })

    for (let index = items.value.length - 1; index >= 0; index--) {
      const item = items.value[index]

      if (item?.kind !== 'call' || item.callId !== callId) continue

      Object.assign(item, settle(event.args ?? item.args))

      return
    }

    // Строки может не оказаться вовсе: битую строку потока разбор пропускает, и
    // потерянный `tool_call` не повод терять ещё и его результат.
    items.value.push({
      id: nextId++,
      kind: 'call',
      callId,
      name: event.name ?? '',
      ...settle(event.args),
    })
  }

  /** Крупное действие собрано и ждёт человека: ход на сервере приостановлен. */
  const startDraft = (event: AssistantEvent) => {
    items.value.push({
      id: nextId++,
      kind: 'draft',
      messageId: event.message_id ?? 0,
      callId: event.call_id ?? '',
      name: event.name ?? '',
      args: event.args,
      state: 'pending',
    })
  }

  /**
   * Конец хода. Обрыв приезжает событием, а не исключением: статус ответа ушёл
   * с первой же строкой потока, и кодом ошибку сообщить уже нечем.
   */
  const finish = (reason?: AssistantDoneReason, message?: string) => {
    isStreaming.value = false

    if (reason !== 'error') return

    isUnavailable.value = true

    if (message) error.value = message
  }

  const apply = (event: AssistantEvent) => {
    if (event.type === 'quota' && event.quota) {
      quota.value = event.quota

      // Счётчик сошёлся с сервером — прежний отказ больше ни о чём не говорит.
      if (event.quota.remaining !== 0) overLimit.value = false
    }

    if (event.type === 'text' && event.text) appendText(event.text)

    if (event.type === 'tool_call') startCall(event)

    if (event.type === 'tool_result') finishCall(event)

    if (event.type === 'draft_action') startDraft(event)

    if (event.type === 'done') finish(event.reason, event.message)
  }

  /** Черновик, на котором стоит ход. Пока он есть, тред ждёт решения человека. */
  const pendingDraft = computed<PanelDraft | null>(() => {
    for (let index = items.value.length - 1; index >= 0; index--) {
      const item = items.value[index]

      if (item?.kind === 'draft' && item.state === 'pending') return item
    }

    return null
  })

  /**
   * Читать ответ сервера до конца, разбирая события.
   *
   * Общий кусок вопроса и подтверждения: обе ручки отвечают одним и тем же
   * потоком — продолжением того же хода.
   */
  const readTurn = async (url: string, body: unknown) => {
    controller = new AbortController()
    isStreaming.value = true
    isUnavailable.value = false

    try {
      await streamTurn(url, {
        body,
        signal: controller.signal,
        onEvent: apply,
        fallbackError: i18n.global.t('assistant.errors.stream'),
      })
    } catch (streamError) {
      // Отмену человек и так видит по остановленной панели — сообщать ему
      // о его же нажатии не о чем.
      if (controller.signal.aborted) return

      // Исчерпанный лимит — не поломка: панель закрывает ввод и говорит, когда
      // счётчик вернётся, а не пугает «ассистент недоступен» с «Повторить»,
      // которое всё равно не сработает.
      if (streamError instanceof AssistantHttpError && streamError.status === 429) {
        overLimit.value = true

        return
      }

      isUnavailable.value = true
      error.value =
        streamError instanceof Error
          ? streamError.message
          : i18n.global.t('assistant.errors.stream')
    } finally {
      controller = null
      isStreaming.value = false
    }
  }

  /**
   * Решение по черновику: применить или отклонить.
   *
   * Отдельный запрос, а не ответ в то же соединение: поток, которым приехал
   * черновик, к моменту решения давно закрыт — человек читал его секунды, а то
   * и минуты. Ход после решения продолжается на сервере и доезжает тем же
   * потоком, поэтому события разбираются тем же `apply`.
   *
   * Строка помечается до запроса: иначе двойной клик по «Применить» ушёл бы
   * дважды, а на сервере второй раз получил бы отказ «подтверждать нечего».
   */
  const decide = async (draft: PanelDraft, approved: boolean, args?: unknown) => {
    if (isStreaming.value || draft.state !== 'pending') return

    draft.state = approved ? 'applied' : 'declined'
    error.value = ''
    hasText.value = false

    // Поправленные человеком аргументы уезжают вместе с решением (DEV-1562):
    // исполняется то, что он видит в таблице, а не то, что предложила модель.
    await readTurn(`${threadPath(threadId.value)}/messages/${draft.messageId}/confirm`, {
      approved,
      ...(args ? { args } : {}),
    })
  }

  const send = async (text: string) => {
    const question = text.trim()

    // Исчерпанный лимит закрывает не поле, а сам вопрос: путей отправки
    // несколько — композер, чипы-примеры, строка ⌘K, — и запрещать в каждом
    // значит однажды забыть.
    if (!question || isStreaming.value || isBlocked.value) return

    // Незакрытый черновик отклоняется перед новым вопросом. Иначе на сервере
    // остался бы вызов инструмента без ответа, а модель отвергает раунд, в
    // котором на вызов не ответили, — следующий вопрос сломал бы весь разговор.
    const waiting = pendingDraft.value

    if (waiting) await decide(waiting, false)

    error.value = ''
    hasText.value = false

    try {
      if (!threadId.value) threadId.value = await createThread(context.value)
    } catch {
      isUnavailable.value = true
      error.value = i18n.global.t('assistant.errors.thread')

      return
    }

    items.value.push({ id: nextId++, kind: 'message', role: 'user', text: question })

    // Дескриптор снимается в момент вопроса, а не в момент создания треда: на
    // доске фильтруют, потом спрашивают — и спрашивают именно про то, что
    // отфильтровали.
    await readTurn(`${threadPath(threadId.value)}/messages`, {
      text: question,
      page_context: page.value,
    })
  }

  /**
   * Повторить после отказа инструмента (макет 8-2b).
   *
   * Повторяется вопрос целиком, а не один вызов: цикл идёт на сервере, и
   * отдельного вызова, к которому можно вернуться, у панели нет. Модель увидит
   * прошлую попытку в треде и либо обойдёт отказ, либо честно повторит его.
   */
  const retry = async () => {
    for (let index = items.value.length - 1; index >= 0; index--) {
      const item = items.value[index]

      if (item?.kind !== 'message' || item.role !== 'user') continue

      await send(item.text)

      return
    }

    // Повторять нечего: ход сорвался до первого вопроса — например, не завёлся
    // разговор. Кнопка тогда просто возвращает панель в рабочее состояние,
    // иначе блок «недоступен» остаётся на экране навсегда.
    isUnavailable.value = false
    error.value = ''
  }

  /**
   * Остановить ход. Сначала ручка на сервере, потом обрыв чтения: наоборот
   * запрос ушёл бы уже отменённым сигналом и не доехал бы вовсе.
   */
  const stop = async () => {
    if (!isStreaming.value) return

    const id = threadId.value

    controller?.abort()
    isStreaming.value = false

    if (!id) return

    try {
      await cancelTurn(id)
    } catch {
      // Ход всё равно перестал показываться; сервер добьёт его сам, когда
      // заметит закрытое соединение.
    }
  }

  /**
   * Новый разговор. Идущий ход при этом останавливается: его ответ приехал бы
   * в тред, которого на экране уже нет.
   */
  const reset = () => {
    void stop()

    threadId.value = ''
    items.value = []
    error.value = ''
    isUnavailable.value = false
    hasText.value = false
  }

  /** Открыть прошлый разговор из истории. */
  const openThread = async (id: string) => {
    reset()
    threadId.value = id

    try {
      const stored = await listMessages(id)

      // Вызовы инструментов в историю не поднимаются: в базе от них остаётся
      // выдача целиком, а строка события — это её пересказ в одну фразу.
      items.value = stored.map((message) => ({ ...message, kind: 'message' as const }))
    } catch {
      error.value = i18n.global.t('assistant.errors.thread')
    }
  }

  return {
    isOpen,
    width,
    page,
    context,
    threadId,
    items,
    pendingDraft,
    quota,
    error,
    isStreaming,
    isUnavailable,
    isBlocked,
    hasText,
    isEmpty,
    ensureQuota,
    open,
    close,
    toggle,
    reset,
    openThread,
    setWidth,
    setPage,
    send,
    retry,
    decide,
    stop,
  }
})
